import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { api } from './api/api-client.config';
import { getStorageItem, setStorageItem } from './lib/localStorage';
import Router from './routes';
import ThemeProvider from './theme';
import ScrollToTop from './components/scroll-to-top';
import { StyledChart } from './components/chart';
import LoginPage from './pages/LoginPage';
import { StorageKeys } from './enums/StorageKeys.enum';

export default function App() {
  const [searchParams] = useSearchParams();
  const [showLogin, setShowLogin] = useState(false);
  const [loading, setLoading] = useState(true);
  const isLoginInProgress = useRef(false);

  const accessToken = getStorageItem(StorageKeys.token);
  const token = searchParams.get('token');

  useEffect(() => {
    if (token && !isLoginInProgress.current) {
      isLoginInProgress.current = true;
      // If there is a token, try to login with it
      api
        .post<{ token: string }, { token: string; name: string; role: string }>(
          `/login-with-token`,
          {
            token,
          }
        )
        .then((data) => {
          setStorageItem(StorageKeys.token, data.token);
          setStorageItem(StorageKeys.name, data.name);
          setStorageItem(StorageKeys.role, data.role);
          setLoading(false);

          // Remove token from URL before reload
          window.history.replaceState({}, '', window.location.pathname);
          window.location.reload();
        })
        .catch((error) => {
          console.error('Login error:', error);
          isLoginInProgress.current = false;
          setLoading(false);
          setShowLogin(true);
        });

      return; // Exit early since we are handling login
    }
    // If there is an access token, show the dashboard
    if (accessToken) {
      setLoading(false);
      return;
    }

    // If there is no token, show the login page
    if (!token || !accessToken) {
      setLoading(false);
      setShowLogin(true);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (showLogin) {
    return (
      <ThemeProvider>
        <LoginPage />
      </ThemeProvider>
    );
  }

  return loading ? (
    <Box
      sx={{ display: 'flex', justifyContent: 'center' }}
      style={{ padding: 220 }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <ThemeProvider>
      <ScrollToTop />
      <StyledChart />
      <Router />
    </ThemeProvider>
  );
}
